import {FC, useState} from "react";
import {useTranslation} from "react-i18next";
import {TabRow} from "../../../tab-content";
import "./OperationalTelecomNumbers.scss";
import {Source} from "@common-core/coat-operational-hierarchy-appsync-model";
import {useLazyQuery} from "@apollo/client";
import {
  LookUpClientEntitiesByIds,
  LookupClientEntitiesByIdsData,
  LookupClientEntitiesByIdsVariables,
  assertClientEntitiesPresent
} from "../../backend";
import {Endpoints} from "../../../runtime";
import {useToastErrorHandler} from "../../../backend";
import {PermissionBasedEditButton} from "../../../action-buttons";
import {OnCompleteCallback} from "../../../utils";
import {
  SimpleTable,
  TableBody,
  TableCell,
  TableRow
} from "@interstate/components/SimpleTable";
import {EDIT_TELECOM_NUMBERS} from "../../../access";
import {ClientPhoneNumber, collectClientPhoneNumbers} from "./phones";
import {EditTelecomNumbersModal} from "./EditTelecomNumbersModal";

export interface OperationalTelecomNumbersProps {
  id: string;
  sources?: Source[];
  currentNumbers?: string[] | null;
  displayEditButton?: boolean;
}

export const OperationalTelecomNumbers: FC<OperationalTelecomNumbersProps> = ({
  id,
  sources,
  currentNumbers,
  displayEditButton = false
}) => {
  const {t} = useTranslation();
  const [availableNumbers, setAvailableNumbers] = useState<ClientPhoneNumber[]>(
    []
  );
  const [editingTelecomNumbers, setEditingTelecomNumbers] =
    useState<boolean>(false);
  const toastError = useToastErrorHandler();

  const [lookUpClientEntities, {loading}] = useLazyQuery<
    LookupClientEntitiesByIdsData,
    LookupClientEntitiesByIdsVariables
  >(LookUpClientEntitiesByIds, {
    context: {
      endpoint: Endpoints.APPSYNC
    },
    fetchPolicy: "network-only"
  });

  const beginEditing = () => {
    if (sources && sources.length > 0) {
      const ids = sources
        .map(source => source.sourceId)
        .filter(id => id != null); // Remove null and undefined values
      lookUpClientEntities({variables: {ids}})
        .then(assertClientEntitiesPresent)
        .then(collectClientPhoneNumbers)
        .then(setAvailableNumbers)
        .then(() => {
          setEditingTelecomNumbers(true);
        })
        .catch(toastError);
    } else {
      setEditingTelecomNumbers(true);
    }
  };

  const finishTelecomNumberEditing: OnCompleteCallback = () => {
    setEditingTelecomNumbers(false);
  };

  return (
    <TabRow
      name={"operational-telecom-numbers"}
      header={
        <h4>
          {t("entity-detail.dealer.phone")}
          {displayEditButton && (
            <PermissionBasedEditButton
              onClick={beginEditing}
              loading={loading}
              qualifier={"telecom-numbers"}
              tooltip={t("entity-detail.dealer.edit-telecom-numbers")}
              inline={false}
              permissions={[EDIT_TELECOM_NUMBERS]}
            />
          )}
        </h4>
      }>
      {editingTelecomNumbers && (
        <EditTelecomNumbersModal
          // This is necessary until we support the editing of multiple numbers
          currentNumber={currentNumbers?.at(0)}
          availableNumbers={availableNumbers}
          onComplete={finishTelecomNumberEditing}
        />
      )}
      <SimpleTable
        id={`operational-telecom-numbers-table-${id}`}
        data-testid={`operational-telecom-numbers-table-${id}`}
        dataDensity={"small"}
        background={"white"}
        className={"operational-telecom-numbers-table"}
        bordered={false}
        hover={false}
        striped={false}>
        <TableBody
          id={`telecom-numbers-table-body-${id}`}
          data-testid={`telecom-numbers-table-body-${id}`}
          className={"telecom-numbers-table-body telecom-numbers-body"}>
          <TableRow className={"telecom-numbers-row"}>
            <TableCell className={"telecom-numbers-field"}>
              {t("entity-detail.dealer.phone")}
            </TableCell>
            <TableCell
              className={"telecom-numbers-value"}
              id={"telecom-numbers-phone-numbers"}>
              <ul className={"phone-numbers"}>
                {currentNumbers?.map((phone: string) => (
                  <li key={phone} className={"phone-number"}>
                    {phone}
                  </li>
                ))}
              </ul>
            </TableCell>
          </TableRow>
        </TableBody>
      </SimpleTable>
    </TabRow>
  );
};
